import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Sidebar from '../mendepartmentdetails/Sidebar';
import SubDeptImage from '../../../assets/image/department/holding-key-lock-flat-illustration_74855-18774.jpg';

function CommunityResilienceRecovery() {
  const [Faq, setFaq] = useState(0); // first dropdown open by default
  const titleRef = useRef(null);
  const [titleColor, setTitleColor] = useState('');

  useEffect(() => {
    if (titleRef.current) {
      const c = window.getComputedStyle(titleRef.current).color;
      setTitleColor(c);
    }
  }, []);

  // shared inline styles (match structure used on other extended pages)
  const liRow = { display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '12px' };
  const icon = { marginTop: '4px', flex: '0 0 auto' };
  const kv = { display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: 0 };
  const keyText = { fontWeight: 700, fontSize: '1.1rem', lineHeight: 1.35, color: '#0f172a' };
  const valText = { fontSize: '1rem', lineHeight: 1.6, color: '#334155' };
  const overviewText = { color: titleColor || '#0f172a', fontSize: '1.05rem', lineHeight: 1.6, margin: '8px 0 16px' };

  const sections = [
    {
      Que: 'Survivor & Household Support',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Disaster Case Management:', 'One-on-one navigation for housing, benefits, documents, and referrals; coordinates across agencies to reduce runaround.'],
            ['Crisis Counseling & MH:', 'Short-term counseling, support groups, and referrals to ongoing behavioral health care.'],
            ['Unmet Needs Committees / LTRGs:', 'Faith/community partners review verified gaps and braid funding, materials, and labor for recovery.'],
          ].map(([k, v], i) => (
            <li key={`hh-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Housing Repair & Rebuild',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Shelter → Housing Transition:', 'Help with temporary housing placements, landlord outreach, and move-in assistance.'],
            ['Home Repair Programs:', 'Volunteer rebuilds, local grants/loans, and coordination with insurance and SBA where eligible.'],
            ['Resilient Standards:', '“Build back safer” guidance (roof tie-downs, elevation, defensible space, backflow valves) to cut future risk.'],
          ].map(([k, v], i) => (
            <li key={`house-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Small Business & Workforce',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Business Recovery:', 'Referrals to SBA and local programs; continuity coaching for cash flow, suppliers, and relocation.'],
            ['Workforce Support:', 'Dislocated worker programs, temporary employment on recovery projects, and upskilling for resilient jobs.'],
            ['Procurement Readiness:', 'Help local vendors register for government and NGO rebuild work.'],
          ].map(([k, v], i) => (
            <li key={`biz-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Infrastructure & Lifelines',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Critical Lifelines:', 'Power, water, wastewater, telecom, transport, healthcare, and public safety—prioritized restoration.'],
            ['Debris Operations:', 'Right-of-way clearance, hazardous waste handling, and private-property debris removal when authorized.'],
            ['Mitigation During Repair:', 'Harden sites while crews are mobilized (elevate utilities, anchor tanks, flood-proof critical rooms).'],
          ].map(([k, v], i) => (
            <li key={`life-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Donations & Volunteers',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['VOAD Coordination:', 'Match offers to needs; avoid unsolicited goods; prioritize requested items and warehouse management.'],
            ['Spontaneous Volunteers:', 'Safe check-in, task matching, training, and demobilization; track hours for potential cost-share.'],
            ['Points of Distribution:', 'Equitable sites for water, food, tarps, hygiene kits; mobile delivery for access barriers.'],
          ].map(([k, v], i) => (
            <li key={`vol-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Community Engagement & Equity',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Trusted Messengers:', 'Partner with neighborhood leaders, clinics, schools, and faith groups for outreach.'],
            ['Language & Access:', 'Translated materials, ASL/CC, large-print, hotline options; accessible recovery centers.'],
            ['Priority Populations:', 'Targeted support for seniors, people with disabilities, rural areas, and low-income households.'],
          ].map(([k, v], i) => (
            <li key={`equity-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Grants & Funding Navigation',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['Public Infrastructure:', 'FEMA PA, state funds, and mitigation add-ons for resilient rebuild.'],
            ['Households & Communities:', 'Charitable funds, local grants, and coordination with federal programs where applicable.'],
            ['Match Strategies:', 'Track volunteer hours, donated equipment, and materials to meet cost-share where allowed.'],
          ].map(([k, v], i) => (
            <li key={`fund-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Preparedness & Capacity Building',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['COOP/COG:', 'Keep essential services running; update plans after each incident.'],
            ['Training & Exercises:', 'Tabletops and functional drills for shelters, debris, and mass care coordination.'],
            ['Resilience Hubs:', 'Community facilities with backup power, cooling/heat, and info during outages.'],
          ].map(([k, v], i) => (
            <li key={`cap-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
    {
      Que: 'Recovery Milestones & Communications',
      Answer: (
        <ul className="list-unstyled list-style">
          {[
            ['From DA to DRCs:', 'Damage assessments → recovery centers → case management → long-term rebuild projects.'],
            ['Transparent Updates:', 'Dashboards, meetings, and mail/SMS updates on timelines, funding, and milestones.'],
            ['After-Action Improvements:', 'Document what worked, fix gaps, and publish improvements for the next event.'],
          ].map(([k, v], i) => (
            <li key={`comm-${i}`} style={liRow}>
              <i className="fa-solid fa-circle-arrow-right" style={icon} />
              <div style={kv}><span style={keyText}>{k}</span><span style={valText}>{v}</span></div>
            </li>
          ))}
        </ul>
      ),
    },
  ];

  return (
    <section className="department-details-section">
      <div className="container">
        <div className="row">

          {/* Sidebar */}
          <div className="col-12 col-lg-4 col-xl-4">
            <Sidebar />
          </div>

          {/* Main Content */}
          <div className="col-lg-8">
            <div className="department-details-imgbox">
              <img src={SubDeptImage} className="img-fluid" alt="Community Resilience and Recovery Programs" />
            </div>

            <div className="department-details-content-box">
              <h4 ref={titleRef} className="department-details-title">Community Resilience and Recovery Programs</h4>

              {/* Overview (always visible, same color as title but smaller) */}
              <p style={overviewText}>
                We coordinate long-term recovery with housing repair, case management, mental health services, and resilient
                rebuilds—linking survivors to resources, restoring lifelines, and helping communities bounce forward.
              </p>
            </div>

            {/* Accordion (expanded information) */}
            <div className="accordian-box">
              {sections.map((item, index) => (
                <div className={`accordian-box-item ${Faq !== index && 'active'}`} key={index}>
                  <div className="accordian-title">
                    <h5>{item.Que}</h5>
                    <i
                      className="fa-solid fa-angle-right"
                      onClick={() => setFaq(index)}
                      role="button"
                      aria-label={`Toggle ${item.Que}`}
                    />
                  </div>

                  {Faq === index && (
                    <div className="accordian-content" style={{ display: 'block' }}>
                      {item.Answer}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="department-details-policy-box">
              <h4>Our Commitment:</h4>
              <p>
                We are dedicated to helping communities recover from adversity and equipping them with tools to face future
                challenges—through equitable access, strong partnerships, and projects that reduce risk next time.
              </p>
            </div>
            <div>
              <Link to="/department/emergency">
                <i className="fa-solid fa-circle-arrow-left" style={{ marginRight: '5px' }} />
                Go Back
              </Link>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}

export default CommunityResilienceRecovery;
